import React, { useEffect, useRef, useState } from "react";

/** Lightweight signature canvas — pointer events, no external lib. */
export default function SignaturePad({ onChange, height = 220 }) {
  const ref = useRef(null);
  const stateRef = useRef({ drawing: false, dirty: false, lastX: 0, lastY: 0 });

  useEffect(() => {
    const canvas = ref.current;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const rect = canvas.getBoundingClientRect();
    canvas.width = Math.floor(rect.width * dpr);
    canvas.height = Math.floor(height * dpr);
    canvas.style.height = `${height}px`;
    const ctx = canvas.getContext("2d");
    ctx.scale(dpr, dpr);
    ctx.fillStyle = "white";
    ctx.fillRect(0, 0, rect.width, height);
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.lineWidth = 2.4;
    ctx.strokeStyle = "#1B2A4A";
  }, [height]);

  const pos = (e) => {
    const r = ref.current.getBoundingClientRect();
    return { x: e.clientX - r.left, y: e.clientY - r.top };
  };

  const onDown = (e) => {
    e.preventDefault();
    const ctx = ref.current.getContext("2d");
    const p = pos(e);
    stateRef.current.drawing = true;
    stateRef.current.lastX = p.x;
    stateRef.current.lastY = p.y;
    ctx.beginPath();
    ctx.moveTo(p.x, p.y);
  };
  const onMove = (e) => {
    if (!stateRef.current.drawing) return;
    e.preventDefault();
    const ctx = ref.current.getContext("2d");
    const p = pos(e);
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
    stateRef.current.dirty = true;
  };
  const onUp = (e) => {
    if (!stateRef.current.drawing) return;
    stateRef.current.drawing = false;
    const url = stateRef.current.dirty ? ref.current.toDataURL("image/png") : null;
    onChange?.(url);
  };

  const clear = () => {
    const canvas = ref.current;
    const ctx = canvas.getContext("2d");
    const r = canvas.getBoundingClientRect();
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.fillStyle = "white";
    ctx.fillRect(0, 0, r.width, height);
    stateRef.current.dirty = false;
    onChange?.(null);
  };

  return (
    <div data-testid="signature-pad">
      <canvas
        ref={ref}
        onPointerDown={onDown}
        onPointerMove={onMove}
        onPointerUp={onUp}
        onPointerLeave={onUp}
        className="w-full bg-white rounded-xl border-2 border-white/30 cursor-crosshair touch-none"
        data-testid="signature-canvas"
      />
      <button
        onClick={clear}
        className="mt-3 text-sm text-white/70 hover:text-white underline"
        data-testid="signature-clear-btn"
        type="button"
      >
        Clear
      </button>
    </div>
  );
}
